import { pushApi } from '@/api/push.api';
import { ApiError } from './errors';

/**
 * A buzz when a new order lands, for a counter nobody is watching.
 *
 * The one event in this app that arrives on its own. Everything else happens
 * because somebody here did something; an order appears because a customer
 * somewhere tapped Place order, and until the shop accepts it they are waiting
 * with no answer. The badge in the sidebar covers the case where the tablet is
 * in front of you — this covers the case where it is not.
 *
 * Deliberately NOT load-bearing. The order is in the queue whether or not a
 * notification arrived; this is the courtesy on top. Every failure here is
 * swallowed for that reason.
 *
 * The browser owns the permission, the service worker owns the display, the
 * backend owns what is sent. This file only joins them up.
 */

export type PushState =
  /** This browser cannot do web push at all. */
  | 'unsupported'
  /** iPad in a tab: push works only once added to the home screen. */
  | 'needs-install'
  /** The server has no VAPID key, so there is nothing to subscribe to. */
  | 'unavailable'
  /** Not asked yet. */
  | 'default'
  /** Refused. Only the person at this browser can undo it, in its settings. */
  | 'denied'
  /** On, and this install is registered with the server. */
  | 'enabled';

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Running as an installed app rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPadOS reports itself as a Mac; the touch points give it away. A counter
  // tablet being an iPad is the common case here, not the exotic one.
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

let cachedKey: string | null | undefined;

async function publicKey(): Promise<string | null> {
  if (cachedKey !== undefined) return cachedKey;
  try {
    cachedKey = (await pushApi.publicKey()).publicKey;
  } catch (error) {
    // A server without the route is "not available", not a failure worth
    // showing anybody. Anything else is real and is rethrown.
    if (error instanceof ApiError && error.status === 404) {
      cachedKey = null;
    } else {
      throw error;
    }
  }
  return cachedKey;
}

/** The URL-safe base64 the server hands out, as the bytes `subscribe` wants. */
function keyBytes(base64: string): Uint8Array<ArrayBuffer> {
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function send(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await pushApi.subscribe({
    endpoint: json.endpoint!,
    keys: { p256dh: json.keys!.p256dh!, auth: json.keys!.auth! },
  });
}

/** Where things stand, without asking anybody anything. */
export async function pushState(): Promise<PushState> {
  if (!isPushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  let key: string | null;
  try {
    key = await publicKey();
  } catch {
    // Offline, or the server is down. Say "not asked" rather than "broken" —
    // this shop may well be offline for an hour and the answer is unchanged.
    return Notification.permission === 'granted' ? 'enabled' : 'default';
  }
  if (!key) return 'unavailable';

  if (Notification.permission !== 'granted') return 'default';
  const registration = await navigator.serviceWorker.ready;
  return (await registration.pushManager.getSubscription()) ? 'enabled' : 'default';
}

/**
 * Asks, subscribes, and tells the server.
 *
 * Must run from a tap: browsers refuse a permission prompt that is not the
 * direct result of one, and a prompt fired on load is the fastest way to earn
 * a permanent "no" from somebody who was busy.
 */
export async function enablePush(): Promise<PushState> {
  if (!isPushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';

  const key = await publicKey();
  if (!key) return 'unavailable';

  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'default';

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(key),
    }));

  await send(subscription);
  return 'enabled';
}

/**
 * Re-registers an existing subscription on every launch.
 *
 * Cheap, and it heals what nobody would otherwise notice: the push service
 * rotated the endpoint, our row was pruned after a failed send, or a different
 * member of staff signed in on this tablet — which on a shared counter device
 * is the normal case rather than the exception.
 */
export async function syncPushSubscription(): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  try {
    const key = await publicKey();
    if (!key) return;
    const registration = await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(key),
      }));
    await send(subscription);
  } catch {
    /* the next launch tries again */
  }
}

/**
 * Stops this device receiving one shop's orders. Called on sign-out, so a
 * counter tablet handed to the next shift does not go on buzzing for whoever
 * was signed in before.
 */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    try {
      await pushApi.unsubscribe(subscription.endpoint);
    } finally {
      await subscription.unsubscribe();
    }
  } catch {
    /* best effort, like the rest of sign-out */
  }
}
